/* w3-product-card custom element.
   On desktop, fetches /products/<handle>?view=options on hover and injects
   the result into .js-product-card-options. The fetched template includes
   a .product-card-options-wrapper with data-state="available" or "oos" so
   CSS can differentiate the two states.

   Mobile: no fetch is initiated (per design — mobile quickbuy works differently).
*/
!function () {
    var DEBUG = false; /* set to true in DevTools console to see fetch logs */

    function log() {
        if (DEBUG && typeof console !== 'undefined') {
            console.log.apply(console, ['[HATSY quickbuy]'].concat([].slice.call(arguments)));
        }
    }
    /* Expose so you can toggle it from DevTools: window.HATSY_QUICKBUY_DEBUG = true */
    Object.defineProperty(window, 'HATSY_QUICKBUY_DEBUG', {
        get: function () { return DEBUG; },
        set: function (v) { DEBUG = !!v; },
        configurable: true,
    });

    class ProductCard extends HTMLElement {
        constructor() {
            super();
            this.bindEvents = this.bindEvents.bind(this);
            this.initColorSwatches = this.initColorSwatches.bind(this);
            this.loadProductCardOptions = this.loadProductCardOptions.bind(this);
            this.mobileBreakpoint = 1024;
            this.isMobile = window.matchMedia(`(max-width: ${this.mobileBreakpoint}px)`).matches;
        }
        connectedCallback() {
            /* Build a list of URL prefixes to try, in priority order.
               The English root URL (no /en prefix) is often the only one that
               works on a single-locale English Shopify store, so we always
               include "" (no prefix) as a fallback. */
            var loc = (typeof Shopify !== 'undefined' && Shopify && Shopify.locale) ? Shopify.locale : '';
            this.urlPrefixes = [];
            if (loc && loc !== 'fr') this.urlPrefixes.push('/' + loc);
            this.urlPrefixes.push(''); /* always try root as last resort */
            if (this.isMobile) return;
            this.initProductCard();
        }
        initProductCard() {
            this.productCard = this.querySelector('.product-card');
            if (!this.productCard) return;
            this.productHandle = this.productCard.dataset.handle;
            this.bindEvents();
            /* Prefetch quickbuy options as soon as the card enters the
               viewport, NOT on hover. By the time the user hovers, the HTML
               is already injected so the white box appears instantly. */
            this.schedulePrefetch();
        }
        schedulePrefetch() {
            this.optionsContainer = this.productCard.querySelector('.js-product-card-options');
            if (!this.optionsContainer) return;
            /* OOS state is pre-rendered: no fetch needed. */
            if (this.optionsContainer.dataset.state === 'oos') return;
            if (this.optionsContainer.dataset.loaded === '1') return;

            var self = this;
            var trigger = function () { self.loadProductCardOptions(); };

            /* If IntersectionObserver isn't available, fetch right away.
               This is rare (only ancient browsers). */
            if (!('IntersectionObserver' in window)) {
                trigger();
                return;
            }

            /* Use a large rootMargin so we start fetching well before the
               card is fully visible — typically 600px below the fold.
               Threshold 0 means fire on first pixel intersection. */
            var io = new IntersectionObserver(function (entries) {
                for (var i = 0; i < entries.length; i++) {
                    if (entries[i].isIntersecting) {
                        io.disconnect();
                        trigger();
                        return;
                    }
                }
            }, { rootMargin: '600px 0px 600px 0px', threshold: 0 });
            io.observe(this.productCard);
        }
        reInitProductCard() {
            this.productCard = this.querySelector('.product-card');
            if (!this.productCard) return;
            this.productHandle = this.productCard.dataset.handle;
            this.loadProductCardOptions();
        }
        bindEvents() {
            this.productCard.addEventListener('mouseenter', this.loadProductCardOptions);
        }
        /* Try each URL prefix in order. Resolve with the first successful
           HTML response, or reject after all attempts fail. */
        fetchWithFallback(handle, viewName) {
            var prefixes = this.urlPrefixes.slice();
            var attempt = (i) => {
                if (i >= prefixes.length) return Promise.reject(new Error('all locale URL attempts failed'));
                var url = prefixes[i] + '/products/' + handle + '?view=' + viewName;
                log('fetch attempt', i + 1, '/', prefixes.length, '->', url);
                return fetch(url).then((response) => {
                    if (!response.ok) {
                        log('  failed with status', response.status);
                        return attempt(i + 1);
                    }
                    return response.text();
                }).catch((err) => {
                    log('  fetch error', err);
                    return attempt(i + 1);
                });
            };
            return attempt(0);
        }
        loadProductCardOptions() {
            this.optionsContainer = this.productCard.querySelector('.js-product-card-options');
            if (!this.productHandle || !this.optionsContainer) return;
            /* OOS products have their pill pre-rendered server-side. Skip fetch. */
            if (this.optionsContainer.dataset.state === 'oos') return;
            /* Don't refetch if we already loaded successfully. */
            if (this.optionsContainer.dataset.loaded === '1') return;
            this.productCard.classList.add('product-card--options-loading');
            log('loading options for handle:', this.productHandle);
            this.fetchWithFallback(this.productHandle, 'options')
                .then((html) => {
                    if (!html) {
                        log('  no html returned');
                        this.productCard.classList.remove('product-card--options-loading');
                        this.injectFallback();
                        return;
                    }
                    var doc;
                    try {
                        doc = new DOMParser().parseFromString(html, 'text/html');
                    } catch (e) {
                        log('  parse error', e);
                        this.productCard.classList.remove('product-card--options-loading');
                        this.injectFallback();
                        return;
                    }
                    var wrapper = doc.querySelector('.product-card-options-wrapper');
                    if (!wrapper) {
                        log('  no .product-card-options-wrapper in response');
                        this.productCard.classList.remove('product-card--options-loading');
                        this.injectFallback();
                        return;
                    }
                    log('  wrapper found, state:', wrapper.dataset.state);
                    this.productCard.classList.remove('product-card--options-loading');
                    this.optionsContainer.innerHTML = '';
                    this.optionsContainer.appendChild(wrapper);
                    this.optionsContainer.dataset.loaded = '1';
                    this.optionsContainer.dataset.state = wrapper.dataset.state || '';
                    this.initColorSwatches();
                    this.productCard.removeEventListener('mouseenter', this.loadProductCardOptions);
                })
                .catch((err) => {
                    log('  all fetches failed', err);
                    this.productCard.classList.remove('product-card--options-loading');
                    this.injectFallback();
                });
        }
        /* If we couldn't load via Section Rendering API, show a graceful
           fallback: a simple "Quickbuy" pill that links to the product page. */
        injectFallback() {
            if (!this.optionsContainer || this.optionsContainer.dataset.loaded === '1') return;
            var productUrl = (this.productCard.querySelector('a[href*="/products/"]') || {}).href || '#';
            var label = 'Quickbuy';
            try {
                var firstAnchor = this.productCard.querySelector('.product-card-title');
                if (firstAnchor) label = 'Quickbuy';
            } catch (e) {}
            this.optionsContainer.innerHTML =
                '<div class="product-card-options-wrapper" data-state="available">' +
                    '<div class="product-card-options-titles u-relative">' +
                        '<div class="product-card-options-title product-card-options-title--quickbuy text-small u-center u-philippine-gray">' + label + '</div>' +
                    '</div>' +
                    '<a class="product-card-options-link" href="' + productUrl + '" aria-label="Quickbuy"></a>' +
                '</div>';
            this.optionsContainer.dataset.loaded = '1';
            this.optionsContainer.dataset.state = 'available';
            this.productCard.removeEventListener('mouseenter', this.loadProductCardOptions);
        }
        initColorSwatches() {
            this.colorSwatches = this.productCard.querySelectorAll('.js-product-card-variant-color');
            if (this.colorSwatches.length === 0) return;
            for (let i = 0; i < this.colorSwatches.length; i++) {
                const swatch = this.colorSwatches[i];
                const handle = swatch.dataset.handle;
                if (!handle) continue;
                swatch.addEventListener('click', () => {
                    this.productCard.classList.add('product-card--loading');
                    this.fetchWithFallback(handle, 'product-card')
                        .then((html) => {
                            if (!html) {
                                this.productCard.classList.remove('product-card--loading');
                                return;
                            }
                            const card = new DOMParser().parseFromString(html, 'text/html').querySelector('.product-card');
                            if (!card) {
                                this.productCard.classList.remove('product-card--loading');
                                return;
                            }
                            this.productCard.classList.remove('product-card--loading');
                            this.productCard.replaceWith(card);
                            if (window.lozadObserver) window.lozadObserver.observe();
                            this.reInitProductCard();
                        })
                        .catch(() => {
                            this.productCard.classList.remove('product-card--loading');
                        });
                });
            }
        }
    }
    if (typeof customElements.get('w3-product-card') === 'undefined') {
        customElements.define('w3-product-card', ProductCard);
    }

    /* ===================================================================
       OOS pill click: open the Restock Rocket / Stoq popup INLINE from
       the collection page instead of navigating to the product page.

       Strategy:
         1. Find a Restock Rocket button that belongs to this product
            (inside the same .product-card, or with a matching product
            handle / product id attribute somewhere on the page).
         2. If found AND clickable, preventDefault on the pill click and
            click the Restock Rocket button programmatically. The Restock
            Rocket popup opens on top of the current page.
         3. If not found, fall through to default link behavior — the
            existing ?stock_alert=1 flow auto-triggers the popup on the
            product page as a safety net.
       =================================================================== */
    function findRestockButtonForCard(card) {
        if (!card) return null;
        /* Look INSIDE the card first — Restock Rocket usually injects a
           hidden container near each card on collection pages. */
        var candidates = card.querySelectorAll(
            '.restock-rocket-button-container button, ' +
            '.restock-rocket-button-container a, ' +
            '.restock-rocket-button, ' +
            '[class*="restock-rocket-button"] button, ' +
            '[class*="restock-rocket-button"] a, ' +
            '[data-restock-rocket-button]'
        );
        for (var i = 0; i < candidates.length; i++) {
            var c = candidates[i];
            /* Skip anything wrapping a product image — that's a card-wrapper,
               not a button. */
            if (c.querySelector && c.querySelector('img, picture')) continue;
            return c;
        }
        return null;
    }

    document.addEventListener('click', function (e) {
        var pill = e.target.closest('.product-card-oos-pill');
        if (!pill) return;
        /* Only intercept if the pill links to ?stock_alert=1 (our flow). */
        var href = pill.getAttribute('href') || '';
        if (href.indexOf('stock_alert=1') === -1) return;

        var card = pill.closest('.product-card');
        if (!card) return;

        var btn = findRestockButtonForCard(card);
        if (!btn) return; /* fall through to navigation */

        /* Found a Restock Rocket trigger inside this card — open the
           popup inline. Make the button briefly clickable (we hid it
           with position: absolute; left: -9999px). Move it back, click
           it, then re-hide on next tick so layout stays clean. */
        e.preventDefault();
        e.stopPropagation();

        /* Cache the inline styles we set on the hidden wrapper so we can
           restore them after the click. Walk up to the
           .restock-rocket-button-container if we're on a child button. */
        var wrapper = btn.closest('.restock-rocket-button-container') || btn;

        var saved = {
            display: wrapper.style.display,
            visibility: wrapper.style.visibility,
            position: wrapper.style.position,
            left: wrapper.style.left,
            top: wrapper.style.top,
            opacity: wrapper.style.opacity,
            pointerEvents: wrapper.style.pointerEvents
        };

        /* Reveal just long enough for the click to register. We DON'T move
           the wrapper visually (no layout shift); we only flip pointer
           events and visibility so the button is dispatchable. */
        wrapper.style.setProperty('pointer-events', 'auto', 'important');
        wrapper.style.setProperty('visibility', 'visible', 'important');

        try {
            btn.click();
        } catch (err) {
            /* If the click failed for any reason, fall back to navigation. */
            window.location.href = href;
            return;
        }

        /* Restore the hidden state on next frame so the button never
           visibly appears on screen. */
        requestAnimationFrame(function () {
            wrapper.style.display = saved.display;
            wrapper.style.visibility = saved.visibility;
            wrapper.style.position = saved.position;
            wrapper.style.left = saved.left;
            wrapper.style.top = saved.top;
            wrapper.style.opacity = saved.opacity;
            wrapper.style.pointerEvents = saved.pointerEvents;
            /* Re-apply the !important hidden state if it was there. */
            if (wrapper.dataset && wrapper.dataset.hatsyHidden === '1') {
                wrapper.style.setProperty('display', 'none', 'important');
                wrapper.style.setProperty('visibility', 'hidden', 'important');
                wrapper.style.setProperty('pointer-events', 'none', 'important');
            }
        });
    }, true);

    /* ===================================================================
       Delegated click handler for desktop quickbuy size buttons.
       Bypasses the <product-form> custom element indirection entirely.
       Reads variant ID from the closest form's hidden input, posts to
       /cart/add.js, then opens the cart drawer.
       =================================================================== */
    document.addEventListener('click', function (e) {
        var btn = e.target.closest('.product-card .product-card-variants .product-card-variant');
        if (!btn) return;
        /* Only handle submit-type buttons inside an injected quickbuy. */
        if (btn.type !== 'submit') return;
        if (btn.classList.contains('product-card-variant--unavailable')) return;
        if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

        e.preventDefault();
        e.stopPropagation();

        /* Find variant id from the nearest form's hidden input. */
        var form = btn.closest('form');
        var variantId = null;
        if (form) {
            var idInput = form.querySelector('input[name="id"]');
            if (idInput) variantId = idInput.value;
        }
        /* Fallback: button's own data-variant-id (some themes have this). */
        if (!variantId && btn.dataset.variantId) variantId = btn.dataset.variantId;
        if (!variantId) {
            log('No variant id found for clicked button', btn);
            return;
        }

        if (btn.classList.contains('button--loading')) return;
        btn.classList.add('button--loading');
        btn.setAttribute('aria-disabled', 'true');

        var Cart = document.querySelector('w3-cart');
        var body = { items: [{ id: parseInt(variantId, 10), quantity: 1 }] };
        if (Cart && typeof Cart.getSectionsToRender === 'function') {
            try {
                body.sections = Cart.getSectionsToRender().map(function (s) { return s.id; });
                body.sections_url = window.location.pathname;
            } catch (err) { /* ignore */ }
        }

        fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/javascript',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(body)
        })
        .then(function (r) { return r.json(); })
        .then(function (data) {
            if (data && data.status) {
                log('Add to cart failed', data);
                return;
            }
            if (Cart && typeof Cart.updateCart === 'function') {
                try { Cart.updateCart(data); } catch (err) { log('updateCart err', err); }
            }
            var openEvt = new CustomEvent('theme:drawer:cart:open');
            document.dispatchEvent(openEvt);
        })
        .catch(function (err) {
            log('Add to cart fetch error', err);
        })
        .finally(function () {
            btn.classList.remove('button--loading');
            btn.removeAttribute('aria-disabled');
        });
    }, true); /* capture phase, so we run before <product-form>'s submit handler */

    /* ===================================================================
       Hide the Restock Rocket "Notify me when available" button INSIDE
       product cards on collection pages. The app injects these as black
       buttons under the price, which breaks our quickbuy pill UX.

       Detection strategy (must be defensive — Restock Rocket also WRAPS
       some product cards with a hidden element that has 'restock-rocket'
       in its class. Don't hide entire wrappers, only leaf buttons.):

         1. Find every leaf-ish element whose visible text reads
            "notify me when available" (or close variants).
         2. Also catch the exact-class containers
            `.restock-rocket-button-container` and `.restock-rocket-button`.
         3. Only hide them when they sit INSIDE a `.product-card` and
            OUTSIDE the main product page's `.product-form` (the alert
            button on the actual product page must remain visible).
       =================================================================== */
    function isInsideProductCard(el) {
        var node = el;
        while (node && node !== document.body) {
            if (node.classList && node.classList.contains('product-card')) return true;
            node = node.parentNode;
        }
        return false;
    }
    function isInsideProductForm(el) {
        var node = el;
        while (node && node !== document.body) {
            if (node.classList && node.classList.contains('product-form')) return true;
            node = node.parentNode;
        }
        return false;
    }
    function hideElement(el) {
        if (!el) return;
        if (el.dataset && el.dataset.hatsyHidden === '1') return;
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('opacity', '0', 'important');
        el.style.setProperty('position', 'absolute', 'important');
        el.style.setProperty('left', '-9999px', 'important');
        el.style.setProperty('top', '-9999px', 'important');
        el.style.setProperty('width', '0', 'important');
        el.style.setProperty('height', '0', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
        el.setAttribute('aria-hidden', 'true');
        if (el.dataset) el.dataset.hatsyHidden = '1';
    }
    var NOTIFY_RE = /^\s*notify\s+me\s+when\s+available\s*$/i;
    function hideStrayRestockButtons() {
        /* Pass 1: exact-class containers — these are the safest targets. */
        var byClass;
        try {
            byClass = document.querySelectorAll(
                '.restock-rocket-button-container, .restock-rocket-button, ' +
                '[class*="restock-rocket-button"], [data-restock-rocket-button]'
            );
        } catch (e) { byClass = []; }
        for (var i = 0; i < byClass.length; i++) {
            var el = byClass[i];
            /* Skip if it's a wrapper around the entire card — those don't
               look like buttons. Heuristic: a button container should be
               relatively small (< 200px tall) and shouldn't contain a
               product image. */
            if (el.querySelector && el.querySelector('img, picture')) continue;
            if (!isInsideProductCard(el)) continue;
            if (isInsideProductForm(el)) continue;
            hideElement(el);
        }
        /* Pass 2: leaf buttons/links/divs in product cards whose visible
           text is exactly "Notify me when available". Walk every product
           card individually so we don't scan the whole document. */
        var cards = document.querySelectorAll('.product-card');
        for (var c = 0; c < cards.length; c++) {
            /* Look at elements that commonly host the button: button, a, div. */
            var candidates = cards[c].querySelectorAll('button, a, div, span');
            for (var k = 0; k < candidates.length; k++) {
                var cand = candidates[k];
                /* Skip our own pill elements — they say "Create an alert", not "Notify me when available". */
                if (cand.classList && (
                    cand.classList.contains('product-card-oos-pill') ||
                    cand.classList.contains('product-card-oos-pill__default') ||
                    cand.classList.contains('product-card-oos-pill__hover') ||
                    cand.classList.contains('js-product-card-options')
                )) continue;
                if (isInsideProductForm(cand)) continue;
                var txt = (cand.textContent || '').trim();
                if (!txt) continue;
                if (txt.length > 80) continue; /* Real CTAs are short */
                if (NOTIFY_RE.test(txt)) {
                    /* Walk up to a sensible container (the button-like
                       ancestor) and hide that. Stop walking once we hit
                       the product card. */
                    var target = cand;
                    var hops = 0;
                    while (target && target.parentNode && hops < 5) {
                        var parent = target.parentNode;
                        if (!parent.classList) break;
                        if (parent.classList.contains('product-card')) break;
                        /* If the parent is essentially just this element
                           (no other significant siblings with text), climb. */
                        var hasOtherTextSibling = false;
                        for (var s = 0; s < parent.children.length; s++) {
                            var sib = parent.children[s];
                            if (sib === target) continue;
                            if ((sib.textContent || '').trim().length > 0) {
                                hasOtherTextSibling = true; break;
                            }
                        }
                        if (hasOtherTextSibling) break;
                        target = parent;
                        hops++;
                    }
                    hideElement(target);
                }
            }
        }
    }
    /* Run multiple times — Restock Rocket injects asynchronously after
       page load. No MutationObserver (interferes with lozad lazy-loading). */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', hideStrayRestockButtons);
    } else {
        hideStrayRestockButtons();
    }
    setTimeout(hideStrayRestockButtons, 500);
    setTimeout(hideStrayRestockButtons, 1500);
    setTimeout(hideStrayRestockButtons, 3500);
    setTimeout(hideStrayRestockButtons, 7000);
}();
